import moment from 'moment-timezone';
import { capitalize } from '@ember/string';
import { ActionInfo } from 'cio/utils/action-info';
import { monthsBetweenDates } from 'cio/utils/journey-metrics';
import { DELIVERY_METRICS_MEASURE_DEFINITIONS, findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import { TimeRange } from 'cio/utils/time';

export enum Grouping {
  Hourly = 'hourly',
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export const ALL_CHANNELS = ['email', 'twilio', 'push', 'webhook', 'slack', 'urban_airship', 'in_app'] as const;
export type Channel = (typeof ALL_CHANNELS)[number];

export enum MetricTypes {
  Analysis = 'analysis',
  Metric = 'metric',
  SplitMetric = 'split_metric',
  TemplateMetric = 'template_metric',
  TrackedLinkMetric = 'tracked_link_metric',
  TrackedResponseMetric = 'tracked_response_metric',
  SegmentMembershipMetric = 'segment_membership_metric',
  JourneyMetric = 'journey_metric',
  DashboardTests = 'dashboard_tests',
  CampaignActionMetrics = 'campaign_action_metrics',
  CampaignChannelMetrics = 'campaign_channel_metrics',
}

export const LABEL_MAP: Record<string, string> = {
  failed: 'errored',
};

export enum Entities {
  ENVIRONMENT = 'environment',
  CAMPAIGN = 'campaign',
  NEWSLETTER = 'newsletter',
  TRANSACTIONAL = 'transactional_message',
  ACTION = 'action',
  TEMPLATE = 'template',
  SEGMENT = 'segment',
}

export enum Periods {
  HOURS = 'hours',
  DAYS = 'days',
  WEEK = 'week',
  WEEKS = 'weeks',
  MONTHS = 'months',
  ALL_TIME = 'all_time',
  CUSTOM = 'custom',
}

export const PERIOD_LABELS = {
  [Periods.HOURS]: 'hourly',
  [Periods.DAYS]: 'daily',
  [Periods.WEEKS]: 'weekly',
  [Periods.MONTHS]: 'monthly',
  [Periods.ALL_TIME]: 'all time',
  [Periods.CUSTOM]: 'daily',
};

export const PERIOD_KEYS = {
  [Periods.HOURS]: Grouping.Hourly,
  [Periods.DAYS]: Grouping.Daily,
  [Periods.WEEK]: Grouping.Daily,
  [Periods.WEEKS]: Grouping.Weekly,
  [Periods.MONTHS]: Grouping.Monthly,
  [Periods.ALL_TIME]: Grouping.Daily,
  [Periods.CUSTOM]: Grouping.Daily,
};

export const REVERSE_PERIOD_KEYS = {
  [Grouping.Hourly]: Periods.HOURS,
  [Grouping.Daily]: Periods.DAYS,
  [Grouping.Weekly]: Periods.WEEKS,
  [Grouping.Monthly]: Periods.MONTHS,
  [Periods.ALL_TIME]: Periods.ALL_TIME,
  [Periods.CUSTOM]: Periods.CUSTOM,
};

// High spam rate warning level, 2%
export const SPAM_WARN_LEVEL = 0.02;

export type StateRange = {
  danger: number;
  warning: number;
};

export const SPAM_RANGE = {
  danger: 0.1,
  warning: 0.06,
};

export const BOUNCED_RANGE = {
  danger: 5,
  warning: 2,
};

// Number of data points in time series
export const PERIOD_SERIES_LENGTHS = {
  [Periods.DAYS]: 30,
  [Periods.WEEKS]: 12,
  [Periods.MONTHS]: 13,
};

export type Measure =
  | 'attempted'
  | 'bounced'
  | 'clicked'
  | 'clicked_to_opened'
  | 'converted'
  | 'created'
  | 'delivered'
  | 'failed'
  | 'opened'
  | 'sent'
  | 'spammed'
  | 'suppressed'
  | 'undeliverable'
  | 'unsubscribed'
  | 'topic_unsubscribed';

export type SplitMetricMeasure = Extract<Measure, 'sent' | 'delivered' | 'opened' | 'clicked' | 'converted'>;

export const MEASURES: Measure[] = [
  'attempted',
  'bounced',
  'clicked',
  'clicked_to_opened',
  'converted',
  'created',
  'delivered',
  'failed',
  'opened',
  'sent',
  'spammed',
  'suppressed',
  'undeliverable',
  'unsubscribed',
  'topic_unsubscribed',
];

export const MEASURE_LABELS = Object.keys(DELIVERY_METRICS_MEASURE_DEFINITIONS).reduce<Record<string, string>>(
  (acc, key) => {
    acc[key] = DELIVERY_METRICS_MEASURE_DEFINITIONS[key].label;
    return acc;
  },
  {},
);

export const SPLIT_METRIC_MEASURES: SplitMetricMeasure[] = ['sent', 'delivered', 'opened', 'clicked', 'converted'];

export const MEASURE_RATE_TITLES: Record<
  Extract<Measure, 'bounced' | 'clicked' | 'converted' | 'failed' | 'opened' | 'sent'>,
  string
> = {
  bounced: 'Bounce Rate',
  clicked: 'Tracked Link Clicks',
  converted: 'Conversion Rate',
  failed: 'Error Rate',
  opened: 'Open Rate',
  sent: 'Success Rate',
};

export function getMakeColumn(actionInfo: ActionInfo, period: string) {
  return function makeColumn(metric: string) {
    let name = capitalize(metric);
    let title = (MEASURE_RATE_TITLES as Record<string, string>)[metric] || `${name} Rate`;
    let label = LABEL_MAP[metric] || metric;
    let { deliveryStates, deliveryActions } = actionInfo;
    let showLink = [...(deliveryStates ?? []), ...(deliveryActions ?? [])].includes(metric);
    let denominator = findDenominator(metric as Measure, MeasureType.DeliveryMetrics, actionInfo.actionType);

    return {
      percent: period === Periods.ALL_TIME ? `${metric}.allTimeRate` : `${metric}.rate`,
      total: period === Periods.ALL_TIME ? `${metric}.allTimeTotal` : `${metric}.total`,
      denominatorTotal:
        denominator != null
          ? period === Periods.ALL_TIME
            ? `${denominator}.allTimeTotal`
            : `${denominator}.total`
          : null,
      showLink: metric === 'sent' || showLink,
      denominator,
      metric,
      title,
      label,
    };
  };
}

// The backend keeps delivery metrics in UTC -4:00, so we need to make sure
// we send the right timestamps to retrieve them from the API, since
// "timeRange" is selected in the user's local timezone
export function timeRangeToStepsParams(
  timeRange: { start?: moment.Moment; end?: moment.Moment } | null,
  timezone = 'Etc/GMT+4',
  diffMeasure = 'days',
) {
  if (!timeRange?.start || !timeRange?.end) {
    return null;
  }

  let { start, end } = timeRange;

  let steps = (end as any).diff(start, diffMeasure);

  let adjustedStart =
    diffMeasure === 'hour' ? moment.tz(start, timezone) : moment.tz(`${start.format('YYYY-MM-DD')} 00:00:00`, timezone);

  return {
    start: adjustedStart.unix(),
    steps,
  };
}

export function buildMomentInLocalTime(date: moment.Moment) {
  return moment().year(date.year()).month(date.month()).date(date.date());
}

export type MetricDataPoint = {
  x: number; // unix timestamp in milliseconds
  y: number | null;
};

export function generateTimestamps(utcRange: Record<'start' | 'end', moment.Moment>, grouping: Grouping) {
  let timestamps: number[] = [];

  if (grouping === Grouping.Daily) {
    let start = moment(utcRange.start).unix();
    let steps = utcRange.end.diff(utcRange.start, 'days');
    timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60) * 1000)];
  } else if (grouping === Grouping.Weekly) {
    let steps = utcRange.end.diff(utcRange.start, 'weeks');
    let start = utcRange.start.unix();
    timestamps = [start * 1000, ...[...new Array(steps)].map((_, i) => (start + (i + 1) * 24 * 60 * 60 * 7) * 1000)];
  } else if (grouping === Grouping.Monthly) {
    timestamps = monthsBetweenDates(utcRange.start, utcRange.end).map(
      (dateString) => moment.utc(dateString).startOf('month').unix() * 1000,
    );
  }

  return timestamps;
}

export function buildMetricGroupingOptions(timeRange: TimeRange) {
  let groupings = [{ value: Grouping.Daily, label: 'Daily', disabled: false }];
  let daysInRange = (timeRange.end?.diff(timeRange.start, 'days') ?? 0) + 1;

  if (daysInRange >= 15) {
    groupings.push({ label: 'Weekly', value: Grouping.Weekly, disabled: false });
  }

  if (daysInRange >= 62) {
    groupings.push({ label: 'Monthly', value: Grouping.Monthly, disabled: false });
  }

  return groupings;
}

// This utility function tells us how to group daily data in order to obtain
// weekly or monthly sums. It returns an array of integers, each of which
// corresponds to a week/month and represents how many days of data belong in
// that bin.
// For example, for the range of 2020-05-04 to 2020-05-31 (4 full weeks) and a
// weekly grouping, it will return [7, 7, 7, 7].
// For a range of 2020-05-04 to 2020-05-28 it will return [7, 7, 7, 4].
// Or for a range of 2020-05-01 to 2020-07-05 in a monthly grouping, we'll
// obtain [31, 30, 5] - which recognizes how many days there are in a given
// month.
// It's also possible to retrieve data from the API that is already summed into
// months (the "months" resolution param). This means we have retrieved "Last 12
// months", so the groups will always be of length 12. And since the data is
// already summed into months, it will be a simple array of 1s.
// Later we take these ranges, and group days into sub-arrays of the sizes given
// us by this function, and then we sum the values within those sub-arrays, to
// obtain weekly or monthly data.
//
// resolution is the param used for the API, and determines whether the data
// returned is already grouped into daily/weekly/monthly bins.
// grouping is a selection in the UI, which allows grouping daily data into
// weekly or monthly ranges for custom date ranges.
export function getGroupingRanges(timeRange: TimeRange, resolution: Periods, grouping: Grouping, timezone = 'Etc/UTC') {
  let range = {
    start: moment.tz(`${timeRange.start?.format('YYYY-MM-DD')} 00:00:00`, timezone),
    end: moment.tz(`${timeRange.end?.format('YYYY-MM-DD')} 00:00:00`, timezone),
  };

  let rangeLengths: number[] = [];

  if (grouping === Grouping.Daily) {
    return [];
  }

  if (grouping === Grouping.Weekly) {
    let steps = range.end.diff(range.start, 'weeks');

    if (steps === 0) {
      // if dates are within one week
      rangeLengths = [range.end.diff(range.start, 'days') + 1];
    } else {
      if (resolution === Periods.WEEKS) {
        rangeLengths = new Array(steps + 1).fill(1);
      } else {
        // For the weekly grouping, we start the week with the first day
        // selected in the timerange. This is different from what we do in other
        // places in the app, where we start weeks on Sunday/Monday (depending
        // on Eastern/UTC timezones).
        // This means that all range lengths except the last week will be 7 days
        // long, while the last week may not always be a full one.
        let diffInDays = range.end.diff(range.start, 'days') + 1;
        let weekCount = Math.floor(diffInDays / 7);

        let remainderDays = diffInDays % 7;
        rangeLengths = [...new Array(weekCount).fill(7)];

        if (remainderDays > 0) {
          rangeLengths.push(remainderDays);
        }
      }
    }
  }

  if (grouping === Grouping.Monthly) {
    let months = monthsBetweenDates(range.start, range.end);
    if (resolution === Periods.MONTHS) {
      rangeLengths = new Array(months.length).fill(1);
    } else {
      let monthsWithDayCounts = months.map((dateString) => ({
        dateString,
        daysInMonth: moment(dateString).daysInMonth(),
      }));

      if (monthsWithDayCounts.length === 1) {
        rangeLengths = [range.end.diff(range.start, 'days') + 1];
      } else {
        rangeLengths = monthsWithDayCounts.map((item, index) => {
          if (index === 0) {
            return item.daysInMonth - range.start.date() + 1;
          }

          if (index === monthsWithDayCounts.length - 1) {
            return range.end.date();
          }

          return item.daysInMonth;
        });
      }
    }
  }

  return rangeLengths;
}
